import React, { useState, useRef } from "react";
import Calendar from "@components/common/Calendar.jsx";
import useAuth from "@hooks/useAuth.jsx";
import moment from "moment";
import CameraAngleFileManager from "./CameraAngleFileManager";
import CameraAngleFileUploadModal from "./CameraAngleFileUploadModal";
import cameraAngleFileApi from '@services/cameraAngleFileApi';
import resourceApi from '@services/resourceApi';
import AlertModal from '@components/common/AlertModal';

/**
 * 카메라 앵글 추가/수정 모달 컴포넌트
 * @param {Object} props - 컴포넌트 속성
 * @param {string} props.title - 모달 제목
 * @param {boolean} props.isVisible - 모달 표시 여부
 * @param {Object} props.inputs - 모달 입력값 상태
 * @param {Function} props.setInputs - 모달 입력값 설정 함수
 * @param {Function} props.onClose - 모달 닫기 함수
 * @param {Function} props.onConfirm - 확인 버튼 클릭 시 실행 함수 (angleFiles 배열도 전달)
 * @param {Function} props.onCheckDuplicate - 중복 확인 함수
 * @param {Array} props.availableBranches - 사용 가능한 지점 목록
 * @param {Array} props.selectedBranches - 선택된 지점 목록
 * @param {Function} props.onAddBranch - 지점 추가 함수
 * @param {Function} props.onRemoveBranch - 지점 제거 함수
 * @param {Function} props.onBranchSelect - 지점 선택 함수
 * @param {string} props.selectedAvailableBranch - 선택된 사용 가능 지점 ID
 * @param {string} props.selectedAddedBranch - 선택된 추가된 지점 ID
 * @param {boolean} props.isEdit - 수정 모드 여부
 * @param {Function} props.onFileImport - 파일 불러오기 함수
 */
const CameraAngleInputModal = ({
    title,
    isVisible,
    inputs,
    setInputs,
    onClose,
    onConfirm,
    onCheckDuplicate,
    availableBranches = [],
    selectedBranches = [],
    onAddBranch,
    onRemoveBranch,
    onBranchSelect,
    selectedAvailableBranch,
    selectedAddedBranch,
    isEdit = false,
    onFileImport,
}) => {

    // useAuth 훅 사용
    const { getUserFromToken } = useAuth();

    // JWT에서 직접 사용자 정보 가져오기
    const user = getUserFromToken();

    // 달력 관련 상태
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarPosition, setCalendarPosition] = useState({
        top: 0,
        left: 0,
    });
    const [activeInput, setActiveInput] = useState(null);

    // 다중 선택을 위한 상태
    const [selectedAvailableBranches, setSelectedAvailableBranches] = useState([]);
    const [selectedAddedBranches, setSelectedAddedBranches] = useState([]);

    // 파일 관리 상태
    const [showFileUploadModal, setShowFileUploadModal] = useState(false);
    const [angleFiles, setAngleFiles] = useState([]);
    const [tempFileCounter, setTempFileCounter] = useState(1); // 임시 파일 ID용
    const [fileRefreshTrigger, setFileRefreshTrigger] = useState(0); // 파일 목록 새로고침 트리거
    const fileManagerRef = useRef(null); // CameraAngleFileManager 참조

    // 알림 모달 상태
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');

    // 파일 업로드 로딩 상태
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

    // 모달이 닫혔다 열릴 때 초기화
    React.useEffect(() => {
        if (isVisible && !isEdit) {
            // 신규 모드에서 모달이 열릴 때 임시 파일 목록 초기화
            setAngleFiles([]);
            setTempFileCounter(1);
        }
    }, [isVisible, isEdit]);

    // 모달 입력값 변경 핸들러
    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (type === 'file') {
            const file = files[0];
            setInputs(prev => ({
                ...prev,
                [name]: file,
                cameraData: file ? file.name : ''
            }));
        } else {
            setInputs(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    // 달력 선택 핸들러
    const handleDateClick = (dateType, event) => {
        const input = event.target;
        const rect = input.getBoundingClientRect();

        setCalendarPosition({
            top: rect.bottom + 5,
            left: rect.left,
        });

        setActiveInput(dateType);
        setShowCalendar(true);
    };

    // 날짜 선택 핸들러
    const handleDateSelect = (startDatetime, endDatetime) => {
        // 활성화된 입력 필드에 따라 시작일 또는 종료일만 업데이트
        if (activeInput === 'startDatetime') {
            // 시작일 입력 필드가 활성화된 경우
            let _startDatetime = '';
            if (moment(startDatetime).isValid()) {
                _startDatetime = moment(startDatetime).format('YYYYMMDD');
            }

            setInputs(prev => ({
                ...prev,
                startDatetime: _startDatetime
            }));
        } else if (activeInput === 'endDatetime') {
            // 종료일 입력 필드가 활성화된 경우
            let _endDatetime = '';
            // 여기서 중요: 종료일의 경우 두 번째 인수인 endDatetime을 사용해야 함
            // 하지만 단일 날짜 선택의 경우 startDatetime에 선택된 날짜가 들어있음
            const dateToUse = endDatetime || startDatetime;
            if (moment(dateToUse).isValid()) {
                _endDatetime = moment(dateToUse).format('YYYYMMDD');
            }

            setInputs(prev => ({
                ...prev,
                endDatetime: _endDatetime
            }));
        }

        setShowCalendar(false);
    };

    // 다중 선택 핸들러
    const handleBranchCheckboxChange = (type, branchId) => {
        if (type === "available") {
            setSelectedAvailableBranches((prev) => {
                if (prev.includes(branchId)) {
                    return prev.filter((id) => id !== branchId);
                } else {
                    return [...prev, branchId];
                }
            });
        } else {
            setSelectedAddedBranches((prev) => {
                if (prev.includes(branchId)) {
                    return prev.filter((id) => id !== branchId);
                } else {
                    return [...prev, branchId];
                }
            });
        }
    };

    // 다중 추가 핸들러
    const handleMultiAddBranch = () => {
        if (selectedAvailableBranches.length > 0) {
            const branchesToAdd = availableBranches.filter((branch) =>
                selectedAvailableBranches.includes(branch.id)
            );
            onAddBranch(branchesToAdd);
            setSelectedAvailableBranches([]);
        }
    };

    // 다중 제거 핸들러
    const handleMultiRemoveBranch = () => {
        if (selectedAddedBranches.length > 0) {
            onRemoveBranch(selectedAddedBranches);
            setSelectedAddedBranches([]);
        }
    };

    // 파일 업로드 성공 핸들러 (신규/수정 모드 구분)
    const handleFileUploadSuccess = (newFile) => {

        if (isEdit && inputs.id) {
            // 수정 모드: API를 통해 실제 등록된 파일 - 목록 새로고침 트리거

            // ref를 통해 직접 새로고침 호출
            if (fileManagerRef.current) {
                fileManagerRef.current.refreshFiles();
            }

            // 추가적으로 트리거도 변경
            setFileRefreshTrigger(prev => prev + 1);

            // 약간의 지연 후 한 번 더 새로고침 (데이터베이스 커밋 대기)
            setTimeout(async () => {
                if (fileManagerRef.current) {
                    fileManagerRef.current.refreshFiles();
                }

                // 테스트: 데이터베이스에 실제로 저장되었는지 확인
                try {
                    await cameraAngleFileApi.test.getFileCount();
                    await cameraAngleFileApi.test.getFilesByAngle(inputs.id);
                } catch (testError) {
                    console.error('테스트 실패:', testError);
                }
            }, 500);

            // 사용자에게 파일 추가 완료 알림
            setAlertMessage('파일이 성공적으로 추가되었습니다.');
            setShowAlertModal(true);

            if (newFile) {
                setAngleFiles(prev => [...prev, newFile]);
            }
        } else {
            // 신규 모드: 임시 파일 목록에 추가
            if (newFile && newFile.fileResourceId) {
                const tempFile = {
                    id: `temp_${tempFileCounter}`,
                    fileName: newFile.fileName || newFile.name,
                    originalFileName: newFile.originalFileName || newFile.name,
                    fileResourceId: newFile.fileResourceId,
                    displayOrder: newFile.displayOrder || (angleFiles.length + 1),
                    isActive: true,
                    isTemp: true
                };

                setAngleFiles(prev => [...prev, tempFile]);
                setTempFileCounter(prev => prev + 1);
            }
        }
        setShowFileUploadModal(false);
    };

    // 파일 목록 변경 핸들러
    const handleFilesChange = (updatedFiles) => {
        setAngleFiles(updatedFiles);
    };

    // 임시 파일 순서 변경 핸들러
    const handleTempFileReorder = (index, direction) => {
        const newFiles = [...angleFiles];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= newFiles.length) return;

        // 순서 변경
        [newFiles[index], newFiles[targetIndex]] = [newFiles[targetIndex], newFiles[index]];

        // displayOrder 재할당
        newFiles.forEach((file, idx) => {
            file.displayOrder = idx + 1;
        });

        setAngleFiles(newFiles);
    };

    // 임시 파일 삭제 핸들러
    const handleTempFileDelete = (fileId) => {
        const newFiles = angleFiles.filter(file => file.id !== fileId);

        // displayOrder 재할당
        newFiles.forEach((file, idx) => {
            file.displayOrder = idx + 1;
        });

        setAngleFiles(newFiles);
    };

    if (!isVisible) return null;

    return (
        <>
            <div className="modal-overlay active" id={isEdit ? "edit-modal" : "create-modal"}>
                <div className="modal-container">
                    <div className="modal-header">
                        <h3 className="modal-title">{title}</h3>
                        <button className="modal-close" onClick={onClose}>
                            &times;
                        </button>
                    </div>
                    <div className="modal-body">
                        {/* 앵글명 */}
                        <div className="modal-form-group">
                            <label className="modal-label">앵글명</label>
                            <div className="modal-input-with-button">
                                <input
                                    type="text"
                                    className="modal-input"
                                    name="angleName"
                                    value={inputs.angleName || ''}
                                    onChange={handleInputChange}
                                    placeholder="앵글명을 입력하세요"
                                />
                                <button
                                    className="modal-btn modal-btn-secondary"
                                    onClick={onCheckDuplicate}
                                >
                                    중복확인
                                </button>
                            </div>
                        </div>

                        {/* 작성자 */}
                        <div className="modal-form-group">
                            <label className="modal-label">작성자</label>
                            <input
                                type="text"
                                className="modal-input"
                                name="author"
                                value={user.name || ''}
                                readOnly
                            />
                        </div>

                        {/* 사용기간 */}
                        <div className="modal-form-group">
                            <label className="modal-label">사용기간</label>
                            <div className="date-range-container">
                                <input
                                    type="text"
                                    className="modal-input date-input"
                                    id="start-date"
                                    name="startDatetime"
                                    value={inputs.startDatetime || ''}
                                    placeholder="시작일"
                                    readOnly
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDateClick("startDatetime", e);
                                    }}
                                />
                                <span className="date-separator">-</span>
                                <input
                                    type="text"
                                    className="modal-input date-input"
                                    id="end-date"
                                    name="endDatetime"
                                    value={inputs.endDatetime || ''}
                                    placeholder="종료일"
                                    readOnly
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDateClick("endDatetime", e);
                                    }}
                                />
                            </div>
                        </div>

                        {/* 앵글 타입 */}
                        <div className="modal-form-group">
                            <label className="modal-label">타입</label>
                            <div className="option-select-container">
                                <select
                                    className="option-select"
                                    name="angleType"
                                    value={inputs.angleType || 'PHOTO'}
                                    onChange={handleInputChange}
                                >
                                    <option value="PHOTO">사진</option>
                                    <option value="VIDEO">동영상</option>
                                    <option value="PHOTO_VIDEO">동영상 + 사진</option>
                                    <option value="GREETING">greeting</option>
                                </select>
                                <i className="ri-arrow-down-s-line select-arrow"></i>
                            </div>
                        </div>

                        {/* 방향 */}
                        <div className="modal-form-group">
                            <label className="modal-label">방향</label>
                            <div className="option-select-container">
                                <select
                                    className="option-select"
                                    name="angleDirection"
                                    value={inputs.angleDirection || 'LANDSCAPE'}
                                    onChange={handleInputChange}
                                >
                                    <option value="LANDSCAPE">가로</option>
                                    <option value="PORTRAIT">세로</option>
                                </select>
                                <i className="ri-arrow-down-s-line select-arrow"></i>
                            </div>
                        </div>

                        {/* 앵글 파일 목록 (다중 파일 방식) */}
                        <div className="modal-form-group">
                            {/* 직접 파일 입력 - 숨김 처리 */}
                            <input
                                type="file"
                                multiple
                                accept=".radm,.radms"
                                onChange={async (e) => {
                                    const files = Array.from(e.target.files);
                                    if (files.length > 0) {
                                        // 파일 확장자 검증
                                        const allowedExtensions = ['.radm', '.radms'];
                                        const invalidFiles = files.filter(file => {
                                            const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
                                            return !allowedExtensions.includes(extension);
                                        });

                                        if (invalidFiles.length > 0) {
                                            setAlertMessage(`허용되지 않는 파일 형식입니다. .radm, .radms 파일만 업로드 가능합니다.`);
                                            setShowAlertModal(true);
                                            e.target.value = ''; // 파일 입력 초기화
                                            return;
                                        }

                                        // 업로드 시작
                                        setIsUploading(true);
                                        setUploadProgress({ current: 0, total: files.length });

                                        // displayOrder는 백엔드에서 자동으로 계산하도록 함

                                        // 각 파일을 실제로 업로드한 후 임시 파일로 처리
                                        for (let i = 0; i < files.length; i++) {
                                            const file = files[i];
                                            try {
                                                setUploadProgress({ current: i + 1, total: files.length });

                                                // ResourceApi를 사용한 실제 파일 업로드
                                                const resource = await resourceApi.uploadFile(file, `앵글파일: ${file.name}`);

                                                // 수정 모드에서 직접 API 호출
                                                if (isEdit && inputs.id) {
                                                    const fileData = {
                                                        fileName: file.name,
                                                        originalFileName: file.name,
                                                        fileResourceId: resource.id,
                                                        // displayOrder는 제거하여 백엔드에서 자동 계산
                                                        isActive: true,
                                                    };

                                                    try {
                                                        const addResponse = await cameraAngleFileApi.addFile(inputs.id, fileData);

                                                        if (addResponse.success) {
                                                            // 성공 시 handleFileUploadSuccess 호출
                                                            handleFileUploadSuccess({
                                                                fileName: file.name,
                                                                originalFileName: file.name,
                                                                fileResourceId: resource.id,
                                                            });
                                                        } else {
                                                            console.error('파일 등록 실패:', addResponse.message);
                                                            setAlertMessage(`${file.name}: ${addResponse.message || '파일 등록에 실패했습니다.'}`);
                                                            setShowAlertModal(true);
                                                        }
                                                    } catch (apiError) {
                                                        console.error('API 호출 실패:', apiError);
                                                        setAlertMessage(`${file.name}: 파일 등록 중 오류가 발생했습니다.`);
                                                        setShowAlertModal(true);
                                                    }
                                                } else {
                                                    // 신규 모드는 기존대로 (프론트에서 displayOrder 계산)
                                                    handleFileUploadSuccess({
                                                        fileName: file.name,
                                                        originalFileName: file.name,
                                                        fileResourceId: resource.id,
                                                        displayOrder: angleFiles.length + i + 1, // 현재 길이 + 인덱스 + 1
                                                    });
                                                }
                                            } catch (error) {
                                                console.error(`파일 업로드 오류: ${file.name}`, error);
                                                setAlertMessage(`${file.name}: 파일 업로드 중 오류가 발생했습니다.`);
                                                setShowAlertModal(true);
                                            }
                                        }

                                        // 업로드 완료
                                        setIsUploading(false);
                                        setUploadProgress({ current: 0, total: 0 });

                                        // 파일 입력 초기화
                                        e.target.value = '';
                                    }
                                }}
                                style={{ display: 'none' }}
                                id="direct-file-input"
                            />

                            {isEdit && inputs.id ? (
                                // 수정 모드: 실제 API 기반 파일 관리
                                <CameraAngleFileManager
                                    ref={fileManagerRef}
                                    angleId={inputs.id}
                                    onFilesChange={handleFilesChange}
                                    isEditing={true}
                                    refreshTrigger={fileRefreshTrigger}
                                />
                            ) : (
                                // 신규 모드: 임시 파일 목록 관리
                                <div className="temp-file-manager">
                                    <div className="file-list-header">
                                        <h3 className="file-list-title">
                                            앵글 파일 목록 {angleFiles.length > 0 && <span className="file-count">({angleFiles.length}개)</span>}
                                        </h3>
                                        <button
                                            className="btn-add-file"
                                            onClick={() => {
                                                const fileInput = document.getElementById('direct-file-input');
                                                if (fileInput) {
                                                    fileInput.click();
                                                }
                                            }}
                                        >
                                            파일 추가
                                        </button>
                                    </div>
                                    {angleFiles.length === 0 ? (
                                        <div className="empty-state">
                                            <p>등록된 앵글 파일이 없습니다.</p>
                                        </div>
                                    ) : (
                                        <div className="temp-file-list">
                                            {angleFiles.map((file, index) => (
                                                <div key={`temp-file-${file.id}-${index}`} className="temp-file-item">
                                                    <div className="file-order">
                                                        <span className="order-number">{file.displayOrder}</span>
                                                        {angleFiles.length > 1 && (
                                                            <div className="order-controls">
                                                                <button
                                                                    className="btn-order"
                                                                    onClick={() => handleTempFileReorder(index, 'up')}
                                                                    disabled={index === 0}
                                                                    title="위로 이동"
                                                                >
                                                                    ▲
                                                                </button>
                                                                <button
                                                                    className="btn-order"
                                                                    onClick={() => handleTempFileReorder(index, 'down')}
                                                                    disabled={index === angleFiles.length - 1}
                                                                    title="아래로 이동"
                                                                >
                                                                    ▼
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="file-info">
                                                        <div className="file-name">{file.fileName}</div>
                                                        {file.originalFileName && file.originalFileName !== file.fileName && (
                                                            <div className="original-name">{file.originalFileName}</div>
                                                        )}
                                                        <div className="file-meta">
                                                            <span className="file-id">ID: {file.fileResourceId}</span>
                                                            <span className="temp-badge">임시</span>
                                                        </div>
                                                    </div>

                                                    <div className="file-actions">
                                                        <button
                                                            className="btn-delete"
                                                            onClick={() => handleTempFileDelete(file.id)}
                                                            title="삭제"
                                                        >
                                                            삭제
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* 배포 여부 */}
                        <div className="modal-form-group toggle-group">
                            <div className="toggle-label">배포 여부</div>
                            <div className="toggle-switch-container">
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        name="isDeployed"
                                        checked={inputs.isDeployed || false}
                                        onChange={handleInputChange}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button
                            className="modal-btn modal-btn-cancel"
                            onClick={onClose}
                            disabled={isUploading}
                        >
                            취소
                        </button>
                        <button
                            className="modal-btn modal-btn-primary"
                            onClick={() => onConfirm(angleFiles)}
                            disabled={isUploading}
                        >
                            확인
                        </button>
                    </div>
                </div>
            </div>

            {/* 달력 컴포넌트 */}
            {showCalendar && (
                <div style={{
                    position: 'fixed',
                    top: `${calendarPosition.top}px`,
                    left: `${calendarPosition.left}px`,
                    zIndex: 10001
                }}>
                    <Calendar
                        onDateSelect={handleDateSelect}
                        onClose={() => setShowCalendar(false)}
                        position={{
                            top: '0px',
                            left: '0px',
                        }}
                    />
                </div>
            )}

            {/* 파일 업로드 모달 */}
            <CameraAngleFileUploadModal
                show={showFileUploadModal}
                angleId={inputs.id}
                onClose={() => setShowFileUploadModal(false)}
                onSuccess={handleFileUploadSuccess}
                isNewMode={!isEdit || !inputs.id}
            />

            {/* 업로드 로딩 오버레이 */}
            {isUploading && (
                <div className="upload-overlay">
                    <div className="upload-loading-content">
                        <div className="loading-spinner"></div>
                        <h3>파일 업로드 중...</h3>
                        <p>
                            {uploadProgress.current > 0 ? (
                                `${uploadProgress.current} / ${uploadProgress.total} 완료`
                            ) : (
                                '업로드를 준비하고 있습니다...'
                            )}
                        </p>
                        <div className="progress-bar">
                            <div
                                className="progress-fill"
                                style={{
                                    width: uploadProgress.total > 0
                                        ? `${(uploadProgress.current / uploadProgress.total) * 100}%`
                                        : '0%'
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* 알림 모달 */}
            <AlertModal
                show={showAlertModal}
                message={alertMessage}
                onConfirm={() => setShowAlertModal(false)}
            />

            <style jsx="true">{`

                .form-hint {
                    color: #666;
                    font-size: 12px;
                    margin-top: 5px;
                    display: block;
                }

                .temp-file-manager {
                    border: 1px solid #e0e0e0;
                    border-radius: 4px;
                    min-height: 100px;
                }

                .file-list-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 15px;
                    border-bottom: 1px solid #e0e0e0;
                }

                .file-list-title {
                    font-size: 16px;
                    font-weight: 600;
                    color: #333;
                    margin: 0;
                }

                .file-count {
                    font-size: 14px;
                    color: #666;
                    font-weight: normal;
                    margin-left: 5px;
                }

                .btn-add-file {
                    background: #4caf50;
                    color: white;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 4px;
                    font-size: 14px;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .btn-add-file:hover {
                    background: #45a049;
                }

                .empty-state {
                    text-align: center;
                    padding: 40px 20px;
                    color: #999;
                }

                .empty-state p {
                    margin: 5px 0;
                }

                .hint {
                    font-size: 14px;
                    color: #666;
                }

                .temp-file-list {
                    max-height: 300px;
                    overflow-y: auto;
                }

                .temp-file-item {
                    display: flex;
                    align-items: center;
                    padding: 15px;
                    background: white;
                    border-bottom: 1px solid #f0f0f0;
                    transition: background-color 0.2s;
                }

                .temp-file-item:last-child {
                    border-bottom: none;
                }

                .temp-file-item:hover {
                    background-color: #f8f9fa;
                }

                .temp-file-item .file-order {
                    display: flex;
                    align-items: center;
                    margin-right: 15px;
                    gap: 10px;
                }

                .temp-file-item .order-number {
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f0f0f0;
                    border-radius: 50%;
                    font-size: 14px;
                    font-weight: 600;
                    color: #666;
                }

                .temp-file-item .order-controls {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                .temp-file-item .btn-order {
                    width: 20px;
                    height: 16px;
                    background: #e0e0e0;
                    border: none;
                    border-radius: 2px;
                    cursor: pointer;
                    font-size: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: background-color 0.2s;
                }

                .temp-file-item .btn-order:hover:not(:disabled) {
                    background: #d0d0d0;
                }

                .temp-file-item .btn-order:disabled {
                    background: #f5f5f5;
                    color: #ccc;
                    cursor: not-allowed;
                }

                .temp-file-item .file-info {
                    flex: 1;
                }

                .temp-file-item .file-name {
                    font-weight: 500;
                    color: #333;
                    margin-bottom: 4px;
                }

                .temp-file-item .original-name {
                    font-size: 13px;
                    color: #666;
                    margin-bottom: 4px;
                }

                .temp-file-item .file-meta {
                    display: flex;
                    gap: 10px;
                    font-size: 12px;
                    color: #999;
                }

                .temp-file-item .file-id {
                    font-family: monospace;
                }

                .temp-badge {
                    background: #2196f3;
                    color: white;
                    padding: 2px 6px;
                    border-radius: 3px;
                    font-size: 11px;
                }

                .temp-file-item .file-actions {
                    display: flex;
                    gap: 8px;
                }

                .temp-file-item .btn-delete {
                    padding: 5px 10px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 13px;
                    transition: background-color 0.2s;
                }

                .temp-file-item .btn-delete {
                    background: #f44336;
                    color: white;
                }

                .temp-file-item .btn-delete:hover {
                    background: #e53935;
                }

                .upload-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10002;
                }

                .upload-loading-content {
                    background: white;
                    padding: 30px;
                    border-radius: 8px;
                    text-align: center;
                    min-width: 300px;
                }

                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    margin: 0 auto 20px;
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid #4caf50;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .upload-loading-content h3 {
                    margin: 0 0 10px;
                    font-size: 18px;
                    color: #333;
                }

                .upload-loading-content p {
                    margin: 0 0 20px;
                    color: #666;
                    font-size: 14px;
                }

                .progress-bar {
                    width: 100%;
                    height: 6px;
                    background: #e0e0e0;
                    border-radius: 3px;
                    overflow: hidden;
                }

                .progress-fill {
                    height: 100%;
                    background: #4caf50;
                    transition: width 0.3s ease;
                }
            `}</style>
        </>
    );
};

export default CameraAngleInputModal;